import $ from 'jquery';

// Share one Maps loader across every dealer block on the page.
let mapsPromise;
const hasPoint = (dealer) => Number.isFinite(dealer.lat) && Number.isFinite(dealer.lng);
const normalize = (value) =>
    String(value)
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
const filterLabels = {
    search: 'Search',
    area: 'Area',
    branch: 'Branch',
    tm: 'TM assigned',
    sales_leader: 'Sales leader',
};

function loadMaps(key) {
    if (mapsPromise) return mapsPromise;

    mapsPromise = new Promise((resolve, reject) => {
        if (window.google && window.google.maps && window.google.maps.Map) {
            resolve(window.google.maps);
            return;
        }
        if (!key) {
            reject(new Error('Map unavailable. Dealer results are still available below.'));
            return;
        }

        let timer;
        const fail = () => {
            window.clearInterval(timer);
            window.clearTimeout(timeout);
            reject(new Error('The map could not load. You can still browse dealers below.'));
        };
        const timeout = window.setTimeout(fail, 15000);
        const ready = () => {
            if (window.google && window.google.maps && window.google.maps.Map) {
                window.clearTimeout(timeout);
                window.clearInterval(timer);
                resolve(window.google.maps);
            }
        };
        timer = window.setInterval(ready, 100);

        // Reuse an API request started by another theme component.
        if (!$('script[src*="maps.googleapis.com/maps/api/js"]').length) {
            window.hvacDealerMapsReady = ready;
            const $script = $('<script>', {
                src: `https://maps.googleapis.com/maps/api/js?${$.param({
                    key,
                    v: 'weekly',
                    loading: 'async',
                    callback: 'hvacDealerMapsReady',
                })}`,
                async: true,
            }).on('error', fail);
            document.head.appendChild($script[0]);
        }
    });
    return mapsPromise;
}

// All dealer text is inserted as text, never as untrusted HTML.
function element(tag, text = '', className = '') {
    return $(`<${tag}>`).text(text).addClass(className);
}

function button(text, action, className = '') {
    return element('button', text, className).attr('type', 'button').on('click', action);
}

function link(text, href) {
    return element('a', text).attr({ href, target: '_blank', rel: 'noopener noreferrer' });
}

function initialize(root) {
    const $root = $(root);
    if ($root.data('initialized')) return;
    const config = JSON.parse($root.attr('data-dealer-map'));
    $root.data('initialized', true).removeAttr('data-dealer-map');

    const dealers = config.dealers;
    const types = $.extend({ unclassified: 'Unclassified' }, config.types);
    const $form = $root.find('.dm-filters');

    // Colors come from validated term metadata, including optional split markers.
    function typeMarker($element, type) {
        return $element.css('--dm-type-background', (config.backgrounds || {})[type] || '#808994');
    }
    function dealerTypes(dealer) {
        return dealer.types.length ? dealer.types : ['unclassified'];
    }

    function typeLabels(dealer) {
        return dealerTypes(dealer).map((type) => types[type]).join(', ');
    }

    function typeBadges(dealer) {
        return dealerTypes(dealer).map((type) =>
            element('span', types[type], 'dm-badge').prepend(
                typeMarker(element('span', '', 'dm-dot'), type)
            )
        );
    }

    const $details = $root.find('.dm-details');
    const $status = $root.find('.dm-map-status');
    const $body = $root.find('tbody');
    const $selects = {};
    ['area', 'branch', 'tm', 'sales_leader'].forEach((name) => {
        $selects[name] = $form.find(`[name="${name}"]`);
    });

    const pageSize = 25;
    let filtered = dealers;
    let page = 1;
    let selected = null;
    let map;
    let maps;
    let Marker;
    let circle;
    let overlays = [];

    function fillSelect(name, rows) {
        const $select = $selects[name];
        const selectedValue = $select.val();
        const options = new Map();
        rows.forEach((dealer) => {
            const value = name === 'sales_leader' ? dealer[name] : dealer[`${name}_id`];
            if (value && dealer[name]) options.set(String(value), dealer[name]);
        });
        $select.empty().append($('<option>', { value: '', text: 'All' }));
        [...options.entries()]
            .sort((a, b) => a[1].localeCompare(b[1]))
            .forEach(([value, text]) => {
                $select.append($('<option>', { value, text }));
            });
        $select.val(options.has(selectedValue) ? selectedValue : '');
        $select.prop('disabled', options.size === 0);
    }

    // Derive child options only from dealers within the selected parent territory.
    function updateTerritories() {
        const area = $selects.area.val();
        const areaRows = dealers.filter((dealer) => !area || dealer.area_id === area);
        fillSelect('branch', areaRows);
        const branch = $selects.branch.val();
        fillSelect(
            'tm',
            areaRows.filter((dealer) => !branch || dealer.branch_id === branch)
        );
    }

    function clearChildren(name) {
        if (name === 'area') $selects.branch.add($selects.tm).val('');
        if (name === 'branch') $selects.tm.val('');
    }

    function resetDetails() {
        selected = null;
        if (circle) circle.setMap(null);
        $details
            .empty()
            .append(
                element('h3', 'Dealer details'),
                element('p', 'Select a map marker or a dealer in the list to see details.')
            );
    }

    function selectDealer(dealer, $trigger) {
        selected = dealer.key;
        const $close = button('×', () => {
            resetDetails();
            renderTable();
            const $rowButton = $body.find('[data-dealer-key]').filter(function () {
                return $(this).attr('data-dealer-key') === dealer.key;
            });
            const $target =
                $trigger && $.contains(document, $trigger[0])
                    ? $trigger
                    : $rowButton.length
                    ? $rowButton
                    : $form.find('[name="search"]');
            $target.trigger('focus');
        }).attr('aria-label', 'Close dealer details');
        const $heading = element('h3', dealer.name).attr('tabindex', -1);
        const $badge = element('div', '', 'dm-type-badges').append(typeBadges(dealer));
        $details
            .empty()
            .append(element('div', '', 'dm-details-heading').append($heading, $close), $badge);

        const $list = element('dl');
        const values = {
            Address: dealer.address,
            City: dealer.city,
            'State / province': dealer.state,
            'Postal code': dealer.zip,
            Area: dealer.area,
            Branch: dealer.branch,
            'TM assigned': dealer.tm,
            'Sales leader': dealer.sales_leader,
            'Coverage radius': `${dealer.radius} ${config.units}`,
            Financing: dealer.financing ? 'Yes' : 'No',
            'Emergency services': dealer.emergency ? 'Yes' : 'No',
            'Extra information': dealer.extra ? 'Yes' : 'No',
        };
        $.each(values, (label, value) => {
            $list.append(element('dt', label), element('dd', value || 'Not provided'));
        });
        if (dealer.phone) {
            const $phone = element('a', dealer.phone).attr(
                'href',
                `tel:${dealer.phone.replace(/[^+\d]/g, '')}`
            );
            $list.append(element('dt', 'Phone'), element('dd').append($phone));
        }
        $details.append($list);
        if (dealer.website) $details.append(link('Visit dealer website ↗', dealer.website));
        if (dealer.googleUrl) $details.append(link('Google business page ↗', dealer.googleUrl));
        if (hasPoint(dealer)) {
            $details.append(
                link(
                    'Directions ↗',
                    `https://www.google.com/maps/dir/?api=1&destination=${dealer.lat},${dealer.lng}`
                )
            );
            if (map) {
                map.panTo({ lat: dealer.lat, lng: dealer.lng });
                if (map.getZoom() < 10) map.setZoom(10);
            }
        } else {
            $details.append(
                element('p', 'This location has no coordinates and is not shown on the map.')
            );
        }
        if (circle) circle.setMap(null);
        if (map && hasPoint(dealer) && dealer.radius > 0) {
            circle = new maps.Circle({
                map,
                center: { lat: dealer.lat, lng: dealer.lng },
                radius: dealer.radius * (config.units === 'mi' ? 1609.344 : 1000),
                fillColor: '#1676b8',
                fillOpacity: 0.08,
                strokeColor: '#1676b8',
                strokeOpacity: 0.5,
                strokeWeight: 1,
                clickable: false,
            });
        }
        renderTable();
        $heading[0].focus({ preventScroll: true });
        if (window.matchMedia('(max-width: 760px)').matches) {
            $heading[0].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }

    function renderTable() {
        $body.empty();
        const count = new Set(filtered.map((dealer) => dealer.id)).size;
        $root.find('.dm-results-heading').text(`Dealers (${count}) · ${filtered.length} locations`);
        filtered.slice((page - 1) * pageSize, page * pageSize).forEach((dealer) => {
            const $row = element('tr').toggleClass('is-selected', dealer.key === selected);
            const $choose = button(dealer.name, () => selectDealer(dealer, $choose)).attr({
                'data-dealer-key': dealer.key,
                'aria-label': `View ${dealer.name}, ${dealer.city || dealer.address || 'location'}`,
            });
            $row.append(element('td').append($choose));
            $row.append(
                element('td', '', 'dm-type-badges').append(typeBadges(dealer))
            );
            [
                dealer.area,
                dealer.branch,
                dealer.tm,
                dealer.sales_leader,
                dealer.city,
                dealer.state,
                dealer.zip,
                `${dealer.radius} ${config.units}`,
                dealer.financing ? 'Yes' : 'No',
                dealer.emergency ? 'Yes' : 'No',
                dealer.google ? 'Yes' : 'No',
            ].forEach((value) => $row.append(element('td', value || '—')));
            $body.append($row);
        });
        if (!filtered.length) {
            $body.append(
                element('tr').append(
                    element(
                        'td',
                        'No dealers match these filters. Try another search or clear the filters.'
                    ).attr('colspan', 13)
                )
            );
        }
        const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
        $root.find('.dm-pagination span').text(`Page ${page} of ${pages}`);
        $root.find('[data-prev]').prop('disabled', page === 1);
        $root.find('[data-next]').prop('disabled', page === pages);
    }

    function fit() {
        if (!map) return;
        const points = filtered.filter(hasPoint);
        if (!points.length) return;
        const bounds = new maps.LatLngBounds();
        points.forEach((dealer) => bounds.extend({ lat: dealer.lat, lng: dealer.lng }));
        map.fitBounds(bounds, 45);
        maps.event.addListenerOnce(map, 'idle', () => {
            if (map.getZoom() > 13) map.setZoom(13);
        });
    }

    // Cluster nearby pins in screen space; identical coordinates open a dealer chooser.
    function renderMarkers() {
        if (!map || !map.getProjection()) return;
        overlays.forEach((marker) => marker.setMap(null));
        overlays = [];
        const projection = map.getProjection();
        const scale = Math.pow(2, map.getZoom());
        const groups = [];
        filtered.filter(hasPoint).forEach((dealer) => {
            const point = projection.fromLatLngToPoint(new maps.LatLng(dealer.lat, dealer.lng));
            const x = point.x * scale;
            const y = point.y * scale;
            const group = groups.find((item) => Math.hypot(item.x - x, item.y - y) < 48);
            if (group) group.items.push(dealer);
            else groups.push({ x, y, items: [dealer] });
        });
        groups.forEach((group) => {
            const items = group.items;
            const position = {
                lat: items.reduce((sum, dealer) => sum + dealer.lat, 0) / items.length,
                lng: items.reduce((sum, dealer) => sum + dealer.lng, 0) / items.length,
            };
            let $pin;
            if (items.length === 1) {
                $pin = button(
                    '',
                    () => selectDealer(items[0], $pin),
                    'dm-pin'
                ).attr('aria-label', `${items[0].name}: ${typeLabels(items[0])}`);
                // Each assigned type gets an equal color segment on the pin.
                $pin.css('--dm-type-background', items[0].pinBackground || '#808994');
            } else {
                $pin = button(
                    String(items.length),
                    () => {
                        const identical = items.every(
                            (dealer) =>
                                Math.abs(dealer.lat - items[0].lat) < 0.00001 &&
                                Math.abs(dealer.lng - items[0].lng) < 0.00001
                        );
                        if (identical || map.getZoom() >= 19) {
                            resetDetails();
                            $details
                                .empty()
                                .append(element('h3', `${items.length} dealer locations`));
                            items.forEach((dealer) => {
                                const $choose = button(dealer.name, () =>
                                    selectDealer(dealer, $choose)
                                );
                                $details.append($choose);
                            });
                            $details.find('button').first().trigger('focus');
                        } else {
                            map.panTo(position);
                            map.setZoom(Math.min(map.getZoom() + 2, 20));
                        }
                    },
                    'dm-cluster'
                ).attr('aria-label', `View ${items.length} dealer locations`);
            }
            overlays.push(new Marker(position, $pin));
        });
    }

    function filter() {
        updateTerritories();
        const query = normalize($form.find('[name="search"]').val() || '').trim();
        const selectedTypes = $form
            .find('[name="type"]:checked')
            .map(function () {
                return this.value;
            })
            .get();
        filtered = dealers.filter((dealer) => {
            if (
                query &&
                !normalize(
                    `${dealer.name} ${dealer.city} ${dealer.zip} ${dealer.address}`
                ).includes(query)
            )
                return false;
            if (selectedTypes.length && !dealer.types.some((type) => selectedTypes.includes(type))) return false;
            return Object.keys($selects).every((name) => {
                const value = $selects[name].val();
                return (
                    !value ||
                    value === (name === 'sales_leader' ? dealer[name] : dealer[`${name}_id`])
                );
            });
        });
        page = 1;
        if (selected && !filtered.some((dealer) => dealer.key === selected)) resetDetails();

        const $chips = $root.find('.dm-selected').empty();
        $form
            .serializeArray()
            .filter((item) => item.value)
            .forEach(({ name, value }) => {
                const text =
                    name === 'type'
                        ? types[value]
                        : `${filterLabels[name]}: ${
                              $selects[name] ? $selects[name].find(':selected').text() : value
                          }`;
                $chips.append(
                    button(`${text} ×`, () => {
                        if (name === 'type') {
                            $form
                                .find('[name="type"]')
                                .filter(function () {
                                    return this.value === value;
                                })
                                .prop('checked', false);
                        } else {
                            $form.find(`[name="${name}"]`).val('');
                            clearChildren(name);
                        }
                        filter();
                    }).attr('aria-label', `Remove filter ${text}`)
                );
            });

        const withPoints = filtered.filter(hasPoint).length;
        $root
            .find('.dm-summary')
            .text(
                `${
                    new Set(filtered.map((dealer) => dealer.id)).size
                } dealers · ${withPoints} mapped locations · ${
                    filtered.length - withPoints
                } without coordinates`
            );
        renderTable();
        renderMarkers();
        fit();
    }

    // Bind once per block; never initialize maps on pages without the block.
    fillSelect('area', dealers);
    fillSelect('sales_leader', dealers);
    $form.on('submit.dealerMap', (event) => event.preventDefault());
    $form.on('input.dealerMap', '[name="search"]', filter);
    $form.on('change.dealerMap', 'select, [name="type"]', function () {
        clearChildren(this.name);
        filter();
    });
    $form.on('reset.dealerMap', () =>
        window.setTimeout(() => {
            resetDetails();
            filter();
        }, 0)
    );
    $root.find('[data-prev]').on('click.dealerMap', () => {
        page--;
        renderTable();
    });
    $root.find('[data-next]').on('click.dealerMap', () => {
        page++;
        renderTable();
    });
    filter();

    loadMaps(config.apiKey)
        .then((api) => {
            maps = api;
            map = new maps.Map($root.find('.dm-map')[0], {
                center: { lat: 49, lng: -110 },
                zoom: 4,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
                maxZoom: 20,
            });
            // Google requires DOM nodes at the overlay boundary; UI uses jQuery elsewhere.
            Marker = class extends maps.OverlayView {
                constructor(position, $node) {
                    super();
                    this.position = new maps.LatLng(position);
                    this.$node = $node;
                    this.setMap(map);
                }
                onAdd() {
                    $(this.getPanes().overlayMouseTarget).append(this.$node);
                    maps.OverlayView.preventMapHitsAndGesturesFrom(this.$node[0]);
                }
                draw() {
                    const point = this.getProjection().fromLatLngToDivPixel(this.position);
                    this.$node.css({ left: point.x, top: point.y });
                }
                onRemove() {
                    this.$node.remove();
                }
            };
            $status.prop('hidden', true);
            map.addListener('idle', renderMarkers);
            fit();
        })
        .catch((error) => $status.text(error.message));
}

export default function dealerMap(blocks) {
    $(blocks).each(function () {
        initialize(this);
    });
}
